import { useEffect, useMemo, useState } from 'react';

import { useAppSelector, useAppDispatch } from 'redux/store';

import useAdvanceTable from 'hooks/useAdvanceTable';
import { useTableController } from 'hooks/useTableController';
import { folderColumn } from 'helpers/folderPaths';
import TableViewLayout from 'layouts/TableViewLayout';

import { getTriggersPaginated } from '../../../../redux/slices/triggersSlice';
import { getActions, ActionButton, ActionDropdown, type ActionEntity } from 'actions';

import '../zones/dropdownStyle.css';

const toActionEntity = (row: any): ActionEntity => ({ type: 'trigger', id: row.trigger_id, zoneName: row.zone_name ?? '', name: row.name });

const ListTriggers = () => {
  const dispatch = useAppDispatch();

  const { triggers, config: headerConfig, filtersConfig, pagination, error, loading } = useAppSelector((state) => state.triggers);

  const userData = useAppSelector((state) => state.auth?.userData);

  const isLoading = loading?.getTriggersPaginated ?? false;

  const pageName = 'triggers';
  const [showSidebar, setShowSidebar] = useState(false);

  useEffect(() => {
    dispatch(getTriggersPaginated({}) as any);
  }, [dispatch]);

  const {
    filters,
    setFilterAndSync,
    resetFilters,
    updateSort,
    updatePage,
    updateLimit,
    limit,
    updateUserSettings,
    generateColumns,
    refresh,
    isSilentRefresh,
    getFormattedData,
  } = useTableController({
    userData,
    pageName,
    filtersConfig,
    headerConfig,
    fetchData: getTriggersPaginated,
    columnFormats: { updated_at: 'datetime' },
  });

  const actionsColumn = {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }: any) => {
      const actions = getActions({ entities: [toActionEntity(row.original)], refresh });
      return <ActionDropdown actions={actions} items={['edit', 'addToTransportTrigger', 'delete']} />;
    },
  };

  const baseColumns = generateColumns((key: string, order: 'asc' | 'desc') => {
    updateSort(key, order);
    updateUserSettings(key, order);
  });

  const columns = [...baseColumns, folderColumn, actionsColumn];

  const formattedData = useMemo(() => getFormattedData(triggers || []), [triggers]);

  const table = useAdvanceTable({
    data: formattedData,
    columns,
    pageSize: limit,
    pagination: true,
    sortable: true,
    selection: true,
    getRowId: (row: any) => String(row.trigger_id),
  });

  const { currentPage, totalPages, totalItems } = pagination;

  const selectionEntities = table.getSelectedRowModel().rows.map((r: any) => toActionEntity(r.original));
  const toolbarActions = getActions({ entities: selectionEntities, refresh });

  return (
    <TableViewLayout
      filtersConfig={filtersConfig}
      filters={filters}
      pageName={pageName}
      columns={(headerConfig ?? []).map((c: any) => ({
        key: c.header_key,
        label: c.label,
      }))}
      pageSize={limit}
      error={error}
      onFilterChange={setFilterAndSync}
      onResetFilters={resetFilters}
      onPageSizeChange={updateLimit}
      currentPage={currentPage}
      totalPages={totalPages}
      totalItems={totalItems}
      onPageChange={updatePage}
      table={table}
      showSidebar={showSidebar}
      setShowSidebar={setShowSidebar}
      onRefresh={refresh}
      isLoading={isLoading}
      isSilentRefresh={isSilentRefresh}
      toolbarButtons={
        <>
          <ActionButton actions={toolbarActions} action="createTrigger" variant="primary" />
          <ActionButton actions={toolbarActions} action="addToTransportTrigger" variant="outline-primary" className="ms-2" label="Add selected to transport" />
        </>
      }
    />
  );
};

export default ListTriggers;
